import { DeepPartial, DeepRequired } from '@/utils/types';
import {
  ArrivalSummary,
  CustomerForecastSummary,
  InventorySummary,
  ISummaryData,
  OrderSummary,
  SalesResultSummary,
} from '../entities/SummaryData';

/**
 * サマリー情報（数量データ集計行の情報）のファクトリークラス
 */
export class SummaryDataFactory {
  /**
   * ArrivalSummaryを生成する
   * @param initialData - 初期値
   */
  private static createArrivalSummary = (initialData: DeepPartial<ArrivalSummary>): ArrivalSummary => {
    const arrivalSummary: Required<ArrivalSummary> = {
      arrivalAmountSummary: initialData.arrivalAmountSummary ?? 0,
      // 対象年月なしは異常値のため-1で除去
      targetMonth: initialData.targetMonth ?? -1,
      targetYear: initialData.targetYear ?? -1,
    };
    return arrivalSummary;
  };

  /**
   * CustomerForecastSummaryを生成する
   * @param initialData - 初期値
   */
  private static createCustomerForecastSummary = (initialData: DeepPartial<CustomerForecastSummary>): CustomerForecastSummary => {
    const customerForecastSummary: Required<CustomerForecastSummary> = {
      customerForecastAmountSummary: initialData.customerForecastAmountSummary ?? 0,
      // 対象/受領年月なしは異常値のため-1で除去
      targetMonth: initialData.targetMonth ?? -1,
      targetYear: initialData.targetYear ?? -1,
      receivedMonth: initialData.receivedMonth ?? -1,
      receivedYear: initialData.receivedYear ?? -1,
    };
    return customerForecastSummary;
  };

  /**
   * InventorySummaryを生成する
   * @param initialData - 初期値
   */
  private static createInventorySummary = (initialData: DeepPartial<InventorySummary>): InventorySummary => {
    const inventorySummary: Required<InventorySummary> = {
      inventoryAmountSummary: initialData.inventoryAmountSummary ?? 0,
      // 対象年月なしは異常値のため-1で除去
      targetMonth: initialData.targetMonth ?? -1,
      targetYear: initialData.targetYear ?? -1,
    };
    return inventorySummary;
  };

  /**
   * OrderSummaryを生成する
   * @param initialData - 初期値
   */
  private static createOrderSummary = (initialData: DeepPartial<OrderSummary>): OrderSummary => {
    const orderSummary: Required<OrderSummary> = {
      preOrderAmountSummary: initialData.preOrderAmountSummary ?? 0,
      deliveredOrderAmountSummary: initialData.deliveredOrderAmountSummary ?? 0,
      // 対象年月なしは異常値のため-1で除去
      targetMonth: initialData.targetMonth ?? -1,
      targetYear: initialData.targetYear ?? -1,
    };
    return orderSummary;
  };

  /**
   * SalesResultSummaryを生成する
   * @param initialData - 初期値
   */
  private static createSalesResultSummary = (initialData: DeepPartial<SalesResultSummary>): SalesResultSummary => {
    const salesResultSummary: Required<SalesResultSummary> = {
      salesResultAmountSummary: initialData.salesResultAmountSummary ?? 0,
      // 対象年月なしは異常値のため-1で除去
      targetMonth: initialData.targetMonth ?? -1,
      targetYear: initialData.targetYear ?? -1,
    };
    return salesResultSummary;
  };

  /**
   * ISummaryDataを生成する
   * @param initialData - 初期値
   */
  public static create = (initialData: DeepPartial<ISummaryData> = {}): ISummaryData => {
    const summaryData: DeepRequired<ISummaryData> = {
      arrivalSummary: (initialData.arrivalSummary ?? []).map(this.createArrivalSummary),
      customerForecastSummary: (initialData.customerForecastSummary ?? []).map(this.createCustomerForecastSummary),
      inventorySummary: (initialData.inventorySummary ?? []).map(this.createInventorySummary),
      orderSummary: (initialData.orderSummary ?? []).map(this.createOrderSummary),
      salesResultSummary: (initialData.salesResultSummary ?? []).map(this.createSalesResultSummary),
    };
    return summaryData;
  };
}
